import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { Link } from 'react-router-dom';
import AppContext from '../../../Context/AppContext';
import requestAxios from '../../../util/requestAxios';
import { getExigoUserIdFromSession } from '../../../util/v2Admin';
import {
    getCachedGoalGroups,
    normalizeGoalGroups,
    setCachedGoalGroups,
} from '../goalsCache';
import GoalJourneyLegend from './GoalJourneyLegend';
import GoalJourneyMarker from './GoalJourneyMarker';
import GoalsJourneyPath from './GoalsJourneyPath';
import JourneyDevPanel from './JourneyDevPanel';
import { getFCategoryPastelAccent } from '../fCategoryIcons';
import {
    buildActiveJourneyPath,
    clampPercent,
    clearStoredOverrides,
    getBreakpointId,
    isJourneyDevAvailable,
    placeMarkersAlongPath,
    readJourneyDevEnabled,
    readStoredOverrides,
    resolveJourneyBundle,
    resolveJourneyPoints,
    simplifyStroke,
    writeJourneyDevEnabled,
    writeStoredOverrides,
} from './journeyPointsConfig';
import './GoalsCard.css';
import './JourneyDevPanel.css';

function accentFromHex(hex) {
    if (!hex || typeof hex !== 'string') return '#c9a227';
    const raw = hex.replace('#', '').trim();
    const full =
        raw.length === 3
            ? raw
                  .split('')
                  .map((c) => c + c)
                  .join('')
            : raw;
    if (full.length !== 6) return '#c9a227';
    return `#${full}`;
}

function journeyAccent(category, color) {
    return getFCategoryPastelAccent(category, accentFromHex(color));
}

/** Public asset — mountain plate with no baked-in path. */
const goalsJourneyBackground = `${
    process.env.PUBLIC_URL || ''
}/portal-v2/dashboard/goals-mountain-journey.png`;

/**
 * Dashboard V2 My Goals card — mountain journey summary of Top 7.
 * Full management remains in GoalsWorkspace.
 */
const GoalsCard = ({ onOpenGoals, isActive = true }) => {
    const { checkUserSession } = useContext(AppContext);
    const session = checkUserSession();
    const { token, userId } = session || {};
    const exigoUserId = getExigoUserIdFromSession(session) || userId;
    const cached = userId ? getCachedGoalGroups(userId) : null;
    const [goals, setGoals] = useState(() =>
        cached?.inProgress ? cached.inProgress.slice(0, 7) : []
    );
    const [loading, setLoading] = useState(() => !cached?.inProgress);
    const [errorMsg, setErrorMsg] = useState('');
    const wasInactiveRef = useRef(false);
    const cardRef = useRef(null);
    const stageRef = useRef(null);

    const [devEnabled, setDevEnabled] = useState(() =>
        isJourneyDevAvailable(exigoUserId) ? readJourneyDevEnabled() : false
    );
    const [overrides, setOverrides] = useState(() =>
        isJourneyDevAvailable(exigoUserId) ? readStoredOverrides() : {}
    );
    const [viewportWidth, setViewportWidth] = useState(() =>
        typeof window !== 'undefined' ? window.innerWidth : 1440
    );
    const [cardSize, setCardSize] = useState({ w: 0, h: 0 });
    const [drawMode, setDrawMode] = useState(false);
    const [liveStroke, setLiveStroke] = useState(null);
    const [drawStatus, setDrawStatus] = useState('');
    const [activeGoalRank, setActiveGoalRank] = useState(null);
    const [isMobileLayout, setIsMobileLayout] = useState(() =>
        typeof window !== 'undefined'
            ? window.matchMedia('(max-width: 767px)').matches
            : false
    );
    const strokeRef = useRef([]);
    const activeClearTimerRef = useRef(null);
    const isMobileLayoutRef = useRef(isMobileLayout);
    const autoTourPausedUntilRef = useRef(0);
    const autoTourEnabledRef = useRef(true);
    isMobileLayoutRef.current = isMobileLayout;

    const setActiveRank = useCallback((rank) => {
        if (activeClearTimerRef.current) {
            clearTimeout(activeClearTimerRef.current);
            activeClearTimerRef.current = null;
        }
        setActiveGoalRank(rank);
        // Brief pause so manual hover/tap is not instantly overwritten
        autoTourPausedUntilRef.current = Date.now() + 4500;
    }, []);

    const clearActiveRank = useCallback(() => {
        // Keep a highlighted goal during auto-tour / mobile sticky select
        if (isMobileLayoutRef.current || autoTourEnabledRef.current) return;
        if (activeClearTimerRef.current) {
            clearTimeout(activeClearTimerRef.current);
        }
        activeClearTimerRef.current = setTimeout(() => {
            setActiveGoalRank(null);
            activeClearTimerRef.current = null;
        }, 80);
    }, []);

    const breakpointId = getBreakpointId(viewportWidth);
    const journeyBundle = useMemo(
        () => resolveJourneyBundle(breakpointId, overrides),
        [breakpointId, overrides]
    );
    const journeyPoints = journeyBundle.points;
    const pathSamples = journeyBundle.pathSamples;

    const loadGoals = useCallback(
        async ({ silent = false } = {}) => {
            if (!token || !userId) return;
            if (!silent) {
                const hit = getCachedGoalGroups(userId);
                if (hit?.inProgress) {
                    setGoals(hit.inProgress.slice(0, 7));
                    setLoading(false);
                } else {
                    setLoading(true);
                }
                setErrorMsg('');
            }
            const { error, data } = await requestAxios({
                url: `goals/GetUserGoalsGroupedByStatus/${userId}`,
                token,
            });
            if (!error && data) {
                const groups = normalizeGoalGroups(data);
                setCachedGoalGroups(userId, groups);
                setGoals(groups.inProgress.slice(0, 7));
                if (silent) setErrorMsg('');
            } else if (!silent && !getCachedGoalGroups(userId)) {
                setErrorMsg('Could not load goals.');
            }
            if (!silent) setLoading(false);
        },
        [token, userId]
    );

    useEffect(() => {
        loadGoals();
    }, [loadGoals]);

    useEffect(() => {
        if (!isActive) {
            wasInactiveRef.current = true;
            return;
        }
        if (!wasInactiveRef.current) return;
        wasInactiveRef.current = false;
        loadGoals({ silent: true });
    }, [isActive, loadGoals]);

    useEffect(() => {
        if (!isJourneyDevAvailable(exigoUserId)) return undefined;
        const onResize = () => setViewportWidth(window.innerWidth);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [exigoUserId]);

    useEffect(() => {
        if (!isJourneyDevAvailable(exigoUserId)) {
            setDevEnabled(false);
            return;
        }
        setDevEnabled(readJourneyDevEnabled());
    }, [exigoUserId, isActive]);

    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) {
            return undefined;
        }
        const mq = window.matchMedia('(max-width: 767px)');
        const onChange = () => setIsMobileLayout(mq.matches);
        onChange();
        if (mq.addEventListener) mq.addEventListener('change', onChange);
        else mq.addListener(onChange);
        return () => {
            if (mq.removeEventListener) {
                mq.removeEventListener('change', onChange);
            } else {
                mq.removeListener(onChange);
            }
        };
    }, []);

    useEffect(() => {
        const el = cardRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver((entries) => {
            const rect = entries[0]?.contentRect;
            if (!rect) return;
            setCardSize({
                w: Math.round(rect.width),
                h: Math.round(rect.height),
            });
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    /* Mobile: Goals card height = Action Tracker × 1.1 */
    useEffect(() => {
        const goalsEl = cardRef.current;
        if (!isMobileLayout || !goalsEl) {
            if (goalsEl) {
                goalsEl.style.minHeight = '';
                goalsEl.style.height = '';
            }
            return undefined;
        }
        const mid = goalsEl.closest('.dv2-mid');
        const trackerEl = mid?.querySelector('.dv2-tracker');
        if (!trackerEl || typeof ResizeObserver === 'undefined') {
            return undefined;
        }
        const syncHeight = () => {
            const h = trackerEl.getBoundingClientRect().height;
            if (!h) return;
            const next = `${Math.round(h * 1.1)}px`;
            goalsEl.style.minHeight = next;
            goalsEl.style.height = next;
        };
        syncHeight();
        const ro = new ResizeObserver(syncHeight);
        ro.observe(trackerEl);
        return () => {
            ro.disconnect();
            goalsEl.style.minHeight = '';
            goalsEl.style.height = '';
        };
    }, [isMobileLayout, loading, goals.length]);

    useEffect(
        () => () => {
            if (activeClearTimerRef.current) {
                clearTimeout(activeClearTimerRef.current);
            }
        },
        []
    );

    const persistOverrides = useCallback((next) => {
        setOverrides(next);
        writeStoredOverrides(next);
    }, []);

    const persistBundle = useCallback(
        (patch) => {
            const current = resolveJourneyBundle(breakpointId, overrides);
            persistOverrides({
                ...overrides,
                [breakpointId]: {
                    points: patch.points ?? current.points,
                    pathSamples:
                        patch.pathSamples !== undefined
                            ? patch.pathSamples
                            : current.pathSamples,
                },
            });
        },
        [breakpointId, overrides, persistOverrides]
    );

    const updatePoint = useCallback(
        (rank, patch) => {
            const current = resolveJourneyBundle(breakpointId, overrides);
            const nextPoints = current.points.map((p) =>
                Number(p.rank) === Number(rank) ? { ...p, ...patch } : p
            );
            persistBundle({ points: nextPoints });
        },
        [breakpointId, overrides, persistBundle]
    );

    const clientToPercent = useCallback((clientX, clientY) => {
        // Prefer the mountain stage; on mobile it matches the full card photo box
        const stage = stageRef.current || cardRef.current;
        if (!stage) return null;
        const rect = stage.getBoundingClientRect();
        if (!rect.width || !rect.height) return null;
        return {
            x: clampPercent(((clientX - rect.left) / rect.width) * 100),
            y: clampPercent(((clientY - rect.top) / rect.height) * 100),
        };
    }, []);

    const handleDevDrag = useCallback(
        (rank, clientX, clientY) => {
            if (drawMode) return;
            const pt = clientToPercent(clientX, clientY);
            if (!pt) return;
            updatePoint(rank, pt);
        },
        [clientToPercent, drawMode, updatePoint]
    );

    const handleToggleDev = useCallback((enabled) => {
        writeJourneyDevEnabled(enabled);
        setDevEnabled(enabled);
        if (!enabled) {
            setDrawMode(false);
            setLiveStroke(null);
            strokeRef.current = [];
        }
    }, []);

    const handleResetBreakpoint = useCallback(() => {
        const next = { ...overrides };
        delete next[breakpointId];
        persistOverrides(next);
    }, [breakpointId, overrides, persistOverrides]);

    const handleResetAll = useCallback(() => {
        clearStoredOverrides();
        setOverrides({});
        setLiveStroke(null);
        strokeRef.current = [];
    }, []);

    const commitDrawnPath = useCallback(
        (rawPoints) => {
            const simplified = simplifyStroke(rawPoints, 0.4, 64);
            if (!simplified || simplified.length < 2) {
                setDrawStatus('Need at least 2 points — keep clicking the path');
                return false;
            }
            const current = resolveJourneyBundle(breakpointId, overrides);
            const nextPoints = placeMarkersAlongPath(
                current.points,
                simplified
            );
            persistOverrides({
                ...overrides,
                [breakpointId]: {
                    points: nextPoints,
                    pathSamples: simplified,
                },
            });
            setLiveStroke(null);
            strokeRef.current = [];
            setDrawMode(false);
            setDrawStatus(
                `Saved! pathSamples=${simplified.length}. Copy all breakpoints JSON now.`
            );
            return true;
        },
        [breakpointId, overrides, persistOverrides]
    );

    /** Click-to-place waypoints (more reliable than freehand drag). */
    const handleDrawClick = useCallback(
        (e) => {
            if (!drawMode) return;
            e.preventDefault();
            e.stopPropagation();
            const pt = clientToPercent(e.clientX, e.clientY);
            if (!pt) {
                setDrawStatus('Could not read click position — try again');
                return;
            }
            const next = [...strokeRef.current, pt];
            strokeRef.current = next;
            setLiveStroke(next);
            setDrawStatus(
                next.length < 2
                    ? `Point 1 @ ${pt.x}, ${pt.y} — click more points toward the summit`
                    : `${next.length} points — click more, or press Finish path`
            );
        },
        [clientToPercent, drawMode]
    );

    const handleFinishDrawnPath = useCallback(() => {
        if (strokeRef.current.length < 2) {
            setDrawStatus('Click at least 2 points on the mountain first');
            return;
        }
        commitDrawnPath(strokeRef.current);
    }, [commitDrawnPath]);

    const handleUndoDrawPoint = useCallback(() => {
        const next = strokeRef.current.slice(0, -1);
        strokeRef.current = next;
        setLiveStroke(next.length ? next : null);
        setDrawStatus(
            next.length
                ? `${next.length} points — click more, or Finish path`
                : 'Draw mode — click the mountain to place path points'
        );
    }, []);

    const handleClearPath = useCallback(() => {
        persistBundle({ pathSamples: null });
        setLiveStroke(null);
        strokeRef.current = [];
        setDrawStatus('Cleared drawn path');
    }, [persistBundle]);

    const handlePlaceMarkersOnPath = useCallback(() => {
        const current = resolveJourneyBundle(breakpointId, overrides);
        if (!current.pathSamples?.length) {
            setDrawStatus('No saved path yet — finish a drawn path first');
            return;
        }
        const nextPoints = placeMarkersAlongPath(
            current.points,
            current.pathSamples
        );
        persistBundle({ points: nextPoints });
        setDrawStatus('Markers placed along saved path');
    }, [breakpointId, overrides, persistBundle]);

    const orderedGoals = useMemo(() => {
        const list = Array.isArray(goals) ? goals.slice(0, 7) : [];
        return [...list].sort((a, b) => {
            const ao = Number(a.goalOrder) || 99;
            const bo = Number(b.goalOrder) || 99;
            return ao - bo;
        });
    }, [goals]);

    const showDev = isJourneyDevAvailable(exigoUserId);

    const activePoints = useMemo(() => {
        if (showDev && devEnabled) return journeyPoints;
        return journeyPoints.slice(0, Math.max(orderedGoals.length, 0));
    }, [journeyPoints, orderedGoals.length, showDev, devEnabled]);

    const pathD = useMemo(() => {
        // While actively drawing, keep the previous gold path; live stroke is separate.
        return buildActiveJourneyPath(activePoints, pathSamples);
    }, [activePoints, pathSamples]);

    const openGoals =
        typeof onOpenGoals === 'function' ? onOpenGoals : undefined;

    const markerSource = useMemo(() => {
        if (showDev && devEnabled) {
            return journeyPoints.map((stop, index) => {
                const goal = orderedGoals[index];
                return {
                    stop,
                    goal,
                    goalNumber: goal
                        ? Number(goal.goalOrder) || index + 1
                        : stop.rank,
                    key:
                        goal?.id != null
                            ? `g-${goal.id}`
                            : `dev-${stop.rank}`,
                };
            });
        }
        return orderedGoals.map((goal, index) => {
            const stop =
                journeyPoints[index] ||
                journeyPoints[journeyPoints.length - 1];
            return {
                stop,
                goal,
                goalNumber: Number(goal.goalOrder) || index + 1,
                key: goal.id != null ? `g-${goal.id}` : `g-i-${index}`,
            };
        });
    }, [showDev, devEnabled, journeyPoints, orderedGoals]);

    const showJourney =
        (!loading && orderedGoals.length > 0) || (showDev && devEnabled);

    const legendItems = useMemo(() => {
        return markerSource
            .filter(({ goal }) => goal || (showDev && devEnabled))
            .map(({ stop, goal, goalNumber }) => {
                const isOolaOne =
                    stop?.type === 'oolaOne' || goalNumber === 1;
                return {
                    rank: stop?.rank ?? goalNumber,
                    goalNumber,
                    title: goal?.text || `Goal ${goalNumber}`,
                    category:
                        goal?.areaName ||
                        'Field',
                    accentColor: journeyAccent(
                        goal?.areaName,
                        goal?.color
                    ),
                    isOolaOne,
                };
            })
            .sort((a, b) => a.goalNumber - b.goalNumber);
    }, [markerSource, showDev, devEnabled]);

    const mobileLegendItems = useMemo(() => {
        if (!legendItems.length) return [];
        const selected =
            legendItems.find((item) => item.rank === activeGoalRank) ||
            legendItems[0];
        return selected ? [selected] : [];
    }, [legendItems, activeGoalRank]);

    /* Auto-tour selection: goal 7 → 1 → loop (all breakpoints) */
    useEffect(() => {
        if (!legendItems.length) {
            autoTourEnabledRef.current = false;
            return undefined;
        }
        if (showDev && devEnabled) {
            autoTourEnabledRef.current = false;
            return undefined;
        }

        const reduceMotion =
            typeof window !== 'undefined' &&
            window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        const sequence = [...legendItems].sort(
            (a, b) => (b.goalNumber || 0) - (a.goalNumber || 0)
        );
        if (!sequence.length) return undefined;

        autoTourEnabledRef.current = !reduceMotion;

        if (reduceMotion) {
            setActiveGoalRank(sequence[sequence.length - 1].rank);
            return undefined;
        }

        setActiveGoalRank(sequence[0].rank);

        const STEP_MS = 2600;
        const timer = window.setInterval(() => {
            if (Date.now() < autoTourPausedUntilRef.current) return;
            setActiveGoalRank((current) => {
                const curIdx = sequence.findIndex((s) => s.rank === current);
                const next = ((curIdx < 0 ? 0 : curIdx) + 1) % sequence.length;
                return sequence[next].rank;
            });
        }, STEP_MS);

        return () => {
            window.clearInterval(timer);
            autoTourEnabledRef.current = false;
        };
    }, [legendItems, showDev, devEnabled]);

    return (
        <>
            <section
                ref={cardRef}
                className={`dv2-goals dv2-goals--journey dv2-glass${
                    showDev && devEnabled ? ' dv2-goals--journey-dev' : ''
                }${showDev && devEnabled && drawMode ? ' is-drawing' : ''}${
                    activeGoalRank != null ? ' has-active-goal' : ''
                }${isMobileLayout ? ' dv2-goals--mobile-focus' : ''}`}
            >
                <div className="dv2-gjourney__scene" aria-hidden="true">
                    <div
                        className="dv2-gjourney__photo"
                        style={{
                            backgroundImage: `url(${goalsJourneyBackground})`,
                        }}
                    />
                    <div className="dv2-gjourney__veil" />
                    <div className="dv2-gjourney__vignette" />
                    <div className="dv2-gjourney__glass-rim" />
                </div>

                <div className="dv2-card-head dv2-goals__head">
                    <div className="dv2-goals__head-copy">
                        <h2 className="dv2-section-title">My Goals</h2>
                        <span className="dv2-section-meta dv2-goals__head-meta">
                            Your Top 7
                            {showDev && devEnabled
                                ? ` · DEV ${breakpointId}`
                                : ''}
                        </span>
                    </div>
                    {openGoals ? (
                        <button
                            type="button"
                            className="dv2-card-cta"
                            onClick={openGoals}
                        >
                            View All Goals →
                        </button>
                    ) : (
                        <Link
                            to="/dashboard-v2?goals=1"
                            className="dv2-card-cta"
                        >
                            View All Goals →
                        </Link>
                    )}
                </div>

                <aside
                    className="dv2-goals__float-quote dv2-glass"
                    aria-label="Daily reminder"
                >
                    <div className="dv2-goals__float-quote-icon" aria-hidden>
                        ▲
                    </div>
                    <p>
                        Focus on your journey, not the destination. Progress
                        every day.
                    </p>
                </aside>

                {loading && (
                    <p className="dv2-goals__status">Loading goals…</p>
                )}
                {errorMsg && <p className="dv2-goals__error">{errorMsg}</p>}

                {!loading &&
                    orderedGoals.length === 0 &&
                    !errorMsg &&
                    !(showDev && devEnabled) && (
                    <p className="dv2-goals__empty">
                        No Top 7 goals yet. Open View All Goals to manage them.
                    </p>
                )}

                {showJourney ? (
                    <div ref={stageRef} className="dv2-gjourney">
                        <GoalsJourneyPath
                            pathD={pathD}
                            pathSamples={
                                liveStroke && liveStroke.length >= 2
                                    ? null
                                    : pathSamples
                            }
                            stops={activePoints}
                            debug={showDev && devEnabled && !drawMode}
                            liveStroke={liveStroke}
                        />

                        {showDev && devEnabled && drawMode ? (
                            <div className="dv2-gjourney__draw-banner">
                                CLICK path points: bottom → summit (
                                {liveStroke?.length || 0} pts)
                            </div>
                        ) : null}

                        {showDev && devEnabled ? (
                            <div
                                className={`dv2-gjourney__draw-layer${
                                    drawMode ? ' is-active' : ''
                                }`}
                                style={{
                                    display: drawMode ? 'block' : 'none',
                                }}
                                onClick={handleDrawClick}
                                role="presentation"
                            >
                                {liveStroke && liveStroke.length ? (
                                    <svg
                                        className="dv2-gjourney__draw-preview"
                                        viewBox="0 0 100 100"
                                        preserveAspectRatio="none"
                                        aria-hidden="true"
                                    >
                                        {liveStroke.length >= 2 ? (
                                            <path
                                                d={liveStroke
                                                    .map(
                                                        (p, i) =>
                                                            `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`
                                                    )
                                                    .join(' ')}
                                                fill="none"
                                                stroke="rgba(0, 255, 200, 0.95)"
                                                strokeWidth="3.5"
                                                vectorEffect="non-scaling-stroke"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        ) : null}
                                        {liveStroke.map((p, i) => (
                                            <circle
                                                key={`wp-${i}`}
                                                cx={p.x}
                                                cy={p.y}
                                                r="1.4"
                                                fill="rgba(0, 255, 200, 1)"
                                                stroke="rgba(0,0,0,0.65)"
                                                strokeWidth="0.35"
                                            />
                                        ))}
                                    </svg>
                                ) : null}
                            </div>
                        ) : null}

                        <ol className="dv2-gjourney__markers" aria-label="Goal path markers">
                            {markerSource.map(
                                ({ stop, goal, goalNumber, key }) => {
                                    const isOolaOne =
                                        stop.type === 'oolaOne' ||
                                        goalNumber === 1;
                                    const rank = stop.rank ?? goalNumber;
                                    return (
                                        <GoalJourneyMarker
                                            key={key}
                                            goalNumber={goalNumber}
                                            title={
                                                goal?.text ||
                                                `Goal ${goalNumber}`
                                            }
                                            category={
                                                goal?.areaName ||
                                                'Field'
                                            }
                                            accentColor={journeyAccent(
                                                goal?.areaName,
                                                goal?.color
                                            )}
                                            isOolaOne={isOolaOne}
                                            isActive={activeGoalRank === rank}
                                            onHighlight={() =>
                                                setActiveRank(rank)
                                            }
                                            onClearHighlight={clearActiveRank}
                                            devMode={
                                                showDev &&
                                                devEnabled &&
                                                !drawMode
                                            }
                                            coordLabel={`${stop.x}, ${stop.y}`}
                                            onDevDrag={(cx, cy) =>
                                                handleDevDrag(
                                                    stop.rank,
                                                    cx,
                                                    cy
                                                )
                                            }
                                            style={{
                                                left: `${stop.x}%`,
                                                top: `${stop.y}%`,
                                            }}
                                        />
                                    );
                                }
                            )}
                        </ol>
                    </div>
                ) : null}

                {showJourney && (!(showDev && devEnabled) || isMobileLayout) ? (
                    <GoalJourneyLegend
                        items={
                            isMobileLayout ? mobileLegendItems : legendItems
                        }
                        navItems={isMobileLayout ? legendItems : null}
                        swipeEnabled={isMobileLayout}
                        activeGoalRank={activeGoalRank}
                        onActiveChange={setActiveRank}
                        onClearActive={clearActiveRank}
                    />
                ) : null}
            </section>

            {showDev ? (
                <JourneyDevPanel
                    enabled={devEnabled}
                    onToggleEnabled={handleToggleDev}
                    breakpointId={breakpointId}
                    viewportWidth={viewportWidth}
                    cardSize={cardSize}
                    points={journeyPoints}
                    pathSamples={pathSamples}
                    overrides={overrides}
                    drawMode={drawMode}
                    drawStatus={drawStatus}
                    livePointCount={liveStroke?.length || 0}
                    onToggleDrawMode={(on) => {
                        setDrawMode(on);
                        if (on) {
                            strokeRef.current = [];
                            setLiveStroke(null);
                            setDrawStatus(
                                'Draw mode ON — click the mountain (bottom → summit). You must see a green border.'
                            );
                        } else {
                            setDrawStatus('');
                        }
                    }}
                    onFinishDrawnPath={handleFinishDrawnPath}
                    onUndoDrawPoint={handleUndoDrawPoint}
                    onClearPath={handleClearPath}
                    onPlaceMarkersOnPath={handlePlaceMarkersOnPath}
                    onResetBreakpoint={handleResetBreakpoint}
                    onResetAll={handleResetAll}
                    onSideChange={(rank, side) => updatePoint(rank, { side })}
                    onPointNudge={(rank, dx, dy) => {
                        const current = resolveJourneyPoints(
                            breakpointId,
                            overrides
                        );
                        const hit = current.find(
                            (p) => Number(p.rank) === Number(rank)
                        );
                        if (!hit) return;
                        updatePoint(rank, {
                            x: clampPercent(hit.x + dx),
                            y: clampPercent(hit.y + dy),
                        });
                    }}
                />
            ) : null}
        </>
    );
};

export default GoalsCard;
